import type { AxiosInstance } from 'axios';

export type AssignableStaffMember = {
  id: string;
  name: string;
  role: string;
  initials: string;
};

type AssignableStaffFetchResult =
  | { status: 'success'; data: AssignableStaffMember[] }
  | { status: 'disabled'; data: AssignableStaffMember[] };

type AssignableStaffCacheEntry = {
  staff: AssignableStaffMember[];
  featureDisabled: boolean;
  fetchedAt: number;
};

const assignableStaffCache = new Map<string, AssignableStaffCacheEntry>();

const getInitials = (name: string): string => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '--';
  const first = parts[0]?.charAt(0) || '';
  const second = parts.length > 1 ? parts[1]?.charAt(0) || '' : '';
  return `${first}${second || first}`.toUpperCase();
};

const normalizeAssignableStaff = (rows: any[]): AssignableStaffMember[] =>
  rows
    .map(row => {
      const id = String(row?._id || '').trim();
      const name = String(row?.bp_employee_name || '').trim();
      const role = String(row?.bp_employee_role || '').trim();
      if (!id || !name) return null;
      return {
        id,
        name,
        role,
        initials: getInitials(name),
      } as AssignableStaffMember;
    })
    .filter((row): row is AssignableStaffMember => Boolean(row));

const getLocationAssignableStaffCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_location_assignable_staff_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

export const getCachedLocationAssignableStaff = (locationId: string) => {
  return assignableStaffCache.get(String(locationId || '').trim()) || null;
};

export const clearCachedLocationAssignableStaff = (locationId?: string) => {
  if (!locationId) {
    assignableStaffCache.clear();
    return;
  }
  assignableStaffCache.delete(String(locationId || '').trim());
};

export const getLocationAssignableStaff = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  locationId: string,
): Promise<AssignableStaffFetchResult> => {
  const cacheKey = String(locationId || '').trim();
  try {
    const response = await getLocationAssignableStaffCore(axiosinstance, auth);
    console.log('getLocationAssignableStaff', response);
    const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
    const staff = normalizeAssignableStaff(rows);
    assignableStaffCache.set(cacheKey, {
      staff,
      featureDisabled: false,
      fetchedAt: Date.now(),
    });
    return { status: 'success', data: staff };
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 403) {
      assignableStaffCache.set(cacheKey, {
        staff: [],
        featureDisabled: true,
        fetchedAt: Date.now(),
      });
      return { status: 'disabled', data: [] };
    }
    return Promise.reject(e);
  }
};

export const prefetchLocationAssignableStaff = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  locationId: string,
) => {
  const cacheKey = String(locationId || '').trim();
  if (!cacheKey || !auth?.accessToken) return;
  const cached = assignableStaffCache.get(cacheKey);
  if (cached && cached.staff.length > 0) {
    return;
  }
  try {
    await getLocationAssignableStaff(axiosinstance, auth, cacheKey);
  } catch (e) {
    // Best-effort preload; modal flow handles non-403 failures.
  }
};

// Core API functions: raw HTTP only
const listEmployeesCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_owner_employees_information?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const createEmployeeCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_add_employees_account_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const updateEmployeeCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.patch(
    `/bp_owner_update_employee_profile_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const deleteEmployeeCore = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.delete(
    `/bp_delete_employee_account_information/${employeeId}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const toggleEmployeeVerificationCore = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  shouldActivate: boolean,
  auth: { accessToken?: string | null },
) => {
  const path = shouldActivate
    ? '/bp_employee_activate_account_information'
    : '/bp_employee_deactivate_account_information';
  return axiosinstance.post(
    path,
    JSON.stringify({ bp_employee_id: employeeId }),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const resetEmployeePasswordCore = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_reset_employee_passcode_information`,
    JSON.stringify({ bp_employee_id: employeeId }),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Public wrappers with basic error propagation
export const listEmployees = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await listEmployeesCore(axiosinstance, ownerId, page, limit, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createEmployee = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  try {
    return await createEmployeeCore(axiosinstance, ownerId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateEmployee = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateEmployeeCore(axiosinstance, ownerId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteEmployee = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await deleteEmployeeCore(axiosinstance, employeeId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const toggleEmployeeVerification = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  shouldActivate: boolean,
  auth: { accessToken?: string | null },
) => {
  try {
    return await toggleEmployeeVerificationCore(
      axiosinstance,
      employeeId,
      shouldActivate,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const resetEmployeePassword = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await resetEmployeePasswordCore(axiosinstance, employeeId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

// Payments endpoints
const listEmployeePaymentsCore = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_employee_payments_information/${employeeId}?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const createEmployeePaymentCore = async (
  axiosinstance: AxiosInstance,
  payload: {
    bp_employee_phone_number: string;
    bp_employee_id: string;
    bp_amount_paid: number | string;
    bp_employee_payment_duration_from: string | null;
    bp_employee_payment_duration_to: string | null;
  },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_employee_payment_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Owner password verification for sensitive ops (e.g., payments)
const verifyOwnerPasswordCore = async (
  axiosinstance: AxiosInstance,
  payload: { bp_password: string },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_verify_owner_password_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Single employee details (owner context)
const getOwnerEmployeeDetailsCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  employeeId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_owner_employee_details/${employeeId}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

export const listEmployeePayments = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await listEmployeePaymentsCore(
      axiosinstance,
      employeeId,
      page,
      limit,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createEmployeePayment = async (
  axiosinstance: AxiosInstance,
  payload: {
    bp_employee_phone_number: string;
    bp_employee_id: string;
    bp_amount_paid: number | string;
    bp_employee_payment_duration_from: string | null;
    bp_employee_payment_duration_to: string | null;
  },
  auth: { accessToken?: string | null },
) => {
  try {
    return await createEmployeePaymentCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const verifyOwnerPassword = async (
  axiosinstance: AxiosInstance,
  payload: { bp_password: string },
  auth: { accessToken?: string | null },
) => {
  try {
    return await verifyOwnerPasswordCore(axiosinstance, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getOwnerEmployeeDetails = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  employeeId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getOwnerEmployeeDetailsCore(
      axiosinstance,
      ownerId,
      employeeId,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

// Employee self-service endpoints (employee context)
const getEmployeeProfileDetailsCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`bp_employee_profile_details_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const uploadEmployeeProfilePictureUrlCore = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  url: string,
) => {
  return axiosinstance.patch(
    `/bp_employee_profile_picture_upload`,
    JSON.stringify({
      bp_employee_id: employeeId,
      bp_employee_profile_picture_url: url,
    }),
    {
      headers: { 'Content-Type': 'application/json' },
    },
  );
};

const logoutEmployeeCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  refreshToken: string | null,
) => {
  return axiosinstance.post(
    `bp_employee_log_out_information/${refreshToken}`,
    {},
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

export const getEmployeeProfileDetails = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getEmployeeProfileDetailsCore(axiosinstance, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const uploadEmployeeProfilePictureUrl = async (
  axiosinstance: AxiosInstance,
  employeeId: string,
  url: string,
) => {
  try {
    return await uploadEmployeeProfilePictureUrlCore(
      axiosinstance,
      employeeId,
      url,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const logoutEmployee = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  refreshToken: string | null,
) => {
  try {
    return await logoutEmployeeCore(axiosinstance, auth, refreshToken);
  } catch (e) {
    return Promise.reject(e);
  }
};
